import React from "react";
import {IconLayoutBlocks} from "../../../../../../components/buttons/IconButtons";
import {CustomTextField} from "../../../../../../components/CustomTextField";
import {isNotEmpty} from "../../../../../../Apis/functions";
import {PACKAGING_QUANTITY_LENGTH} from "./Model/Model";

/**
 * Quantities that are expected at the gate of a crates and pallets return.
 *
 * Every quantity is captured the same way, so the section only declares the
 * field each one writes in the form.
 */
export const EXPECTED_QUANTITIES_FIELDS = [
    {id: 'txt_cap_large_boxes', name: 'largeBoxes', trans: 'large_boxes'},
    {id: 'txt_cap_small_boxes', name: 'smallBoxes', trans: 'small_boxes'},
    {id: 'txt_cap_lids_boxes', name: 'lids', trans: 'lids'},
    {id: 'txt_cap_pallets', name: 'pallets', trans: 'pallets'}
];

/**
 * Quantities as the guard captures them at the gate.
 *
 * The quantities are optional for the requester, so which ones belong to the
 * guard is told by the folio itself: the one the requester expected is only
 * read, and the one that was left blank is counted at the gate and required,
 * because the movement is not closed without every packaging counted.
 *
 * @param {Object} item - Folio the movement belongs to.
 *
 * @returns {Array} Quantities as the gate captures them.
 */
export const getGuardQuantityFields = (item) => EXPECTED_QUANTITIES_FIELDS.map((field) => {
    const disabled = isNotEmpty(item?.[field.name]);

    return {...field, disabled, required: !disabled};
});

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @description
 * Renders the quantities expected at the gate of a crates and pallets return,
 * so what arrives at the plant can be confirmed against what is returned.
 *
 * @param {Function} t - Translation function.
 * @param {Object} materialRequest - Current form information.
 * @param {Function} handleChange - Change handler of the form.
 * @param {boolean} emptyFields - Indicates whether the required fields must be highlighted.
 * @param {boolean} required - Indicates whether the quantities have to be captured,
 * because the requester leaves them to the guard and the guard counts them at the gate.
 * A quantity that declares its own configuration is the one that decides it.
 * @param {Array} fields - Configuration of the quantities being captured.
 */
const ExpectedQuantitiesSection = ({
    t,
    materialRequest,
    handleChange,
    emptyFields,
    required = true,
    disabled = false,
    fields = EXPECTED_QUANTITIES_FIELDS
}) => {

    return (
        <div className="border-full-shades-300 background-general-00 border-radius-8px my-3">
            <div className={"d-flex align-items-center p-2"}>
                <div className={"mr-2 d-flex align-items-center"}><IconLayoutBlocks/></div>
                <p className={"fs-14px ibl-font-w-600"}>{t("expected_quantities")}</p>
            </div>
            <div className={"border-bottom-shades-300"}/>

            {/* The label of every field is read above its border, so the row
                leaves it the room every card of the app leaves for it. */}
            <div className={"row input-padding-top pb-3"}>
                {fields.map(({id, name, trans, required: fieldRequired, disabled: fieldDisabled = false}) => {
                    // The quantity the requester already expected is only read,
                    // so the one the guard counts at the gate is the one that
                    // has to be captured before the movement is closed
                    const isDisabled = disabled || fieldDisabled;
                    const isRequired = (fieldRequired ?? required) && !isDisabled;

                    return (
                        // The quantity is read next to the unit it counts, so
                        // two of them are captured per row: the field keeps the
                        // room the unit leaves and the count is displayed whole
                        <div className={"col-sm-12 col-md-6 col-lg-6"} key={name}>
                            <CustomTextField
                                id={id}
                                length={PACKAGING_QUANTITY_LENGTH}
                                name={name}
                                value={materialRequest?.[name]}
                                trans={trans}
                                type={'number'}
                                onChange={handleChange}
                                emptyFields={emptyFields && isRequired}
                                disabled={isDisabled}
                                required={isRequired}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('units')}</p>}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ExpectedQuantitiesSection;
